import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Key, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OPENAI_EMBEDDING_MODELS = [
  { value: "text-embedding-3-small", label: "text-embedding-3-small (Recommended)", description: "Most cost-effective, 1536 dimensions" },
  { value: "text-embedding-3-large", label: "text-embedding-3-large", description: "Highest quality, 3072 dimensions" },
  { value: "text-embedding-ada-002", label: "text-embedding-ada-002 (Legacy)", description: "Older model, 1536 dimensions" },
];

const OPENROUTER_MODELS = [
  // Free Models
  { value: "deepseek/deepseek-r1:free", label: "DeepSeek R1 (Free)", category: "Free" },
  { value: "deepseek/deepseek-chat:free", label: "DeepSeek Chat (Free)", category: "Free" },
  { value: "meta-llama/llama-3.2-3b-instruct:free", label: "Llama 3.2 3B (Free)", category: "Free" },
  { value: "meta-llama/llama-3.1-8b-instruct:free", label: "Llama 3.1 8B (Free)", category: "Free" },
  { value: "google/gemini-2.0-flash-exp:free", label: "Gemini 2.0 Flash (Free)", category: "Free" },
  { value: "qwen/qwen-2.5-7b-instruct:free", label: "Qwen 2.5 7B (Free)", category: "Free" },
  
  // OpenAI Models
  { value: "openai/gpt-4o", label: "OpenAI GPT-4o", category: "OpenAI" },
  { value: "openai/gpt-4o-mini", label: "OpenAI GPT-4o Mini", category: "OpenAI" },
  { value: "openai/gpt-4-turbo", label: "OpenAI GPT-4 Turbo", category: "OpenAI" },
  { value: "openai/gpt-4", label: "OpenAI GPT-4", category: "OpenAI" },
  { value: "openai/gpt-3.5-turbo", label: "OpenAI GPT-3.5 Turbo", category: "OpenAI" },
  { value: "openai/o1", label: "OpenAI o1", category: "OpenAI" },
  { value: "openai/o1-mini", label: "OpenAI o1 Mini", category: "OpenAI" },
  
  // Anthropic Models
  { value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet", category: "Anthropic" },
  { value: "anthropic/claude-3-opus", label: "Claude 3 Opus", category: "Anthropic" },
  { value: "anthropic/claude-3-sonnet", label: "Claude 3 Sonnet", category: "Anthropic" },
  { value: "anthropic/claude-3-haiku", label: "Claude 3 Haiku", category: "Anthropic" },
  
  // Google Models
  { value: "google/gemini-pro-1.5", label: "Gemini Pro 1.5", category: "Google" },
  { value: "google/gemini-flash-1.5", label: "Gemini Flash 1.5", category: "Google" },
  { value: "google/gemini-2.0-flash-thinking-exp", label: "Gemini 2.0 Flash Thinking", category: "Google" },
  
  // DeepSeek Models
  { value: "deepseek/deepseek-r1", label: "DeepSeek R1", category: "DeepSeek" },
  { value: "deepseek/deepseek-chat", label: "DeepSeek Chat", category: "DeepSeek" },
  
  // Meta Llama Models
  { value: "meta-llama/llama-3.3-70b-instruct", label: "Llama 3.3 70B", category: "Meta" },
  { value: "meta-llama/llama-3.1-405b-instruct", label: "Llama 3.1 405B", category: "Meta" },
  { value: "meta-llama/llama-3.1-70b-instruct", label: "Llama 3.1 70B", category: "Meta" },
  { value: "meta-llama/llama-3.1-8b-instruct", label: "Llama 3.1 8B", category: "Meta" },
  
  // Other Popular Models
  { value: "mistralai/mistral-large", label: "Mistral Large", category: "Mistral" },
  { value: "mistralai/mistral-medium", label: "Mistral Medium", category: "Mistral" },
  { value: "mistralai/mixtral-8x7b-instruct", label: "Mixtral 8x7B", category: "Mistral" },
  { value: "cohere/command-r-plus", label: "Command R+", category: "Cohere" },
  { value: "cohere/command-r", label: "Command R", category: "Cohere" },
  { value: "perplexity/llama-3.1-sonar-large-128k-online", label: "Sonar Large (Online)", category: "Perplexity" },
  { value: "x-ai/grok-2", label: "Grok 2", category: "xAI" },
  { value: "qwen/qwen-2.5-72b-instruct", label: "Qwen 2.5 72B", category: "Qwen" },
];

export default function APIKeysSettings() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    openrouter_api_key: "",
    openrouter_provisioning_key: "",
    default_model: "deepseek/deepseek-r1:free",
    openai_api_key: "",
    openai_embedding_model: "text-embedding-3-small",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormData({
        openrouter_api_key: response.data.openrouter_api_key || "",
        openrouter_provisioning_key: response.data.openrouter_provisioning_key || "",
        default_model: response.data.default_model || "deepseek/deepseek-r1:free",
        openai_api_key: response.data.openai_api_key || "",
        openai_embedding_model: response.data.openai_embedding_model || "text-embedding-3-small",
      });
    } catch (error) {
      toast.error("Failed to fetch API keys");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/settings/api-keys`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("API keys updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update API keys");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="api-keys-settings-page">
      <div className="max-w-3xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">API Keys</h1>
          <p className="text-slate-600">Configure your OpenRouter API integration</p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              OpenRouter Configuration
            </CardTitle>
            <CardDescription>
              Set up your OpenRouter API key to access various AI models.
              <a
                href="https://openrouter.ai/docs/quickstart"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-2 text-indigo-600 hover:text-indigo-700 font-medium"
              >
                View Documentation <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          
          {/* Free Models Notice */}
          <div className="px-6 pb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">🆓</span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-900 mb-1">Free Models Available!</h4>
                  <p className="text-xs text-green-800 leading-relaxed">
                    Models marked as <span className="font-mono bg-green-100 px-1 rounded">(Free)</span> are <strong>completely free to use</strong> - no charges applied! 
                    You only need an OpenRouter API key (free to create). Paid models will consume credits from your OpenRouter account.
                  </p>
                  <a
                    href="https://openrouter.ai/models"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 font-medium mt-2"
                  >
                    View pricing for all models <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="openrouter_api_key" className="text-slate-700 font-medium">
                  OpenRouter API Key (Required)
                </Label>
                <Input
                  id="openrouter_api_key"
                  type="password"
                  value={formData.openrouter_api_key}
                  onChange={(e) => setFormData({ ...formData, openrouter_api_key: e.target.value })}
                  placeholder="sk-or-v1-..."
                  className="h-11 border-slate-300 font-mono text-sm"
                  data-testid="openrouter-api-key-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Get your API key from{" "}
                  <a
                    href="https://openrouter.ai/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    OpenRouter Dashboard
                  </a>
                </p>
              </div>

              <div className="space-y-2 border-t border-slate-200 pt-6">
                <Label htmlFor="openrouter_provisioning_key" className="text-slate-700 font-medium">
                  Provisioning API Key (Optional - For Per-Model Stats)
                </Label>
                <Input
                  id="openrouter_provisioning_key"
                  type="password"
                  value={formData.openrouter_provisioning_key}
                  onChange={(e) => setFormData({ ...formData, openrouter_provisioning_key: e.target.value })}
                  placeholder="sk-or-v1-..."
                  className="h-11 border-slate-300 font-mono text-sm"
                  data-testid="openrouter-provisioning-key-input"
                />
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-2">
                  <p className="text-xs text-blue-900 mb-2">
                    <strong>Why add a provisioning key?</strong>
                  </p>
                  <p className="text-xs text-blue-800 leading-relaxed">
                    A provisioning key allows you to see detailed <strong>per-model usage statistics</strong> in the Stats page. 
                    Without it, you'll only see account-level totals. Create one at{" "}
                    <a
                      href="https://openrouter.ai/settings/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium"
                    >
                      OpenRouter Settings → API Keys → Create Provisioning Key
                    </a>
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_model" className="text-slate-700 font-medium">
                  Default Model
                </Label>
                <Select
                  value={formData.default_model}
                  onValueChange={(value) => setFormData({ ...formData, default_model: value })}
                >
                  <SelectTrigger className="h-11 border-slate-300" data-testid="default-model-select">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {/* Free Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-green-700 bg-green-50 sticky top-0">
                      🆓 Free Models
                    </div>
                    {OPENROUTER_MODELS.filter(m => m.category === "Free").map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                    
                    {/* OpenAI Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                      OpenAI
                    </div>
                    {OPENROUTER_MODELS.filter(m => m.category === "OpenAI").map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                    
                    {/* Anthropic Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                      Anthropic
                    </div>
                    {OPENROUTER_MODELS.filter(m => m.category === "Anthropic").map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                    
                    {/* Google Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                      Google
                    </div>
                    {OPENROUTER_MODELS.filter(m => m.category === "Google").map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                    
                    {/* DeepSeek Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                      DeepSeek
                    </div>
                    {OPENROUTER_MODELS.filter(m => m.category === "DeepSeek").map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                    
                    {/* Meta Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                      Meta Llama
                    </div>
                    {OPENROUTER_MODELS.filter(m => m.category === "Meta").map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                    
                    {/* Mistral Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                      Mistral
                    </div>
                    {OPENROUTER_MODELS.filter(m => m.category === "Mistral").map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                    
                    {/* Other Models */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 mt-1">
                      Other Providers
                    </div>
                    {OPENROUTER_MODELS.filter(m => !["Free", "OpenAI", "Anthropic", "Google", "DeepSeek", "Meta", "Mistral"].includes(m.category)).map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Choose the default AI model for your applications. Free models are available at no cost!
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-6"
                  data-testid="save-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* OpenAI Embeddings Configuration */}
        <Card className="border-slate-200 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-green-600" />
              OpenAI Embeddings (For Knowledge Base Search)
            </CardTitle>
            <CardDescription>
              Configure OpenAI API key for semantic search in Knowledge Base. Required for AI Training & Knowledge Base features.
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-2 text-green-600 hover:text-green-700 font-medium"
              >
                Get API Key <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          
          {/* Info Notice */}
          <div className="px-6 pb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">💡</span>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-yellow-900 mb-1">Why Do I Need This?</h4>
                  <p className="text-xs text-yellow-800 leading-relaxed">
                    OpenAI embeddings convert your Knowledge Base content into searchable vectors, enabling <strong>semantic search</strong>.
                    Without this, the AI Training "Test" feature and Knowledge Base search won't work.
                  </p>
                  <p className="text-xs text-yellow-800 leading-relaxed mt-2">
                    <strong>Cost:</strong> Very affordable - ~$0.00002 per 1k tokens. For 100 documents, typically less than $0.10.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="openai_api_key" className="text-slate-700 font-medium">
                  OpenAI API Key (Required for Embeddings)
                </Label>
                <Input
                  id="openai_api_key"
                  type="password"
                  value={formData.openai_api_key}
                  onChange={(e) => setFormData({ ...formData, openai_api_key: e.target.value })}
                  placeholder="sk-proj-..."
                  className="h-11 border-slate-300 font-mono text-sm"
                  data-testid="openai-api-key-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Get your API key from{" "}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:underline"
                  >
                    OpenAI Platform
                  </a>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="openai_embedding_model" className="text-slate-700 font-medium">
                  Embedding Model
                </Label>
                <Select
                  value={formData.openai_embedding_model}
                  onValueChange={(value) => setFormData({ ...formData, openai_embedding_model: value })}
                >
                  <SelectTrigger className="h-11 border-slate-300" data-testid="embedding-model-select">
                    <SelectValue placeholder="Select embedding model" />
                  </SelectTrigger>
                  <SelectContent>
                    {OPENAI_EMBEDDING_MODELS.map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{model.label}</span>
                          <span className="text-xs text-slate-500">{model.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  <strong>Recommended:</strong> text-embedding-3-small for best cost/performance ratio
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6"
                  data-testid="save-openai-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save OpenAI Settings"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}